import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import LoginForm from './components/LoginForm';
import SocialLogin from './components/SocialLogin';
import TrustSignals from './components/TrustSignals';
import UpgradePrompt from './components/UpgradePrompt';

const UserLogin = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  // Mock credentials for demonstration
  const mockCredentials = {
    email: 'demo@cryptopredict.com',
    password: 'demo123'
  };

  useEffect(() => {
    // Show upgrade prompt after 3 seconds for demo purposes
    const timer = setTimeout(() => {
      setShowUpgradePrompt(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const handleLogin = async (formData) => {
    setIsLoading(true);
    setError('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Check mock credentials
      if (formData?.email === mockCredentials?.email && formData?.password === mockCredentials?.password) {
        // Mock successful login
        const userData = {
          id: 1,
          name: 'John Trader',
          email: formData?.email,
          subscriptionTier: 'free',
          loginTime: new Date()?.toISOString()
        };

        // Store user data in localStorage
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('isAuthenticated', 'true');

        // Redirect to dashboard
        window.location.href = '/trading-dashboard';
      } else {
        setError(`Invalid credentials. Use email: ${mockCredentials?.email} and password: ${mockCredentials?.password}`);
      }
    } catch (err) {
      setError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = async (provider) => {
    setIsLoading(true);
    setError('');

    try {
      // Simulate social login
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Mock successful social login
      const userData = {
        id: 2,
        name: `${provider?.charAt(0)?.toUpperCase() + provider?.slice(1)} User`,
        email: `user@${provider}.com`,
        subscriptionTier: 'free',
        loginTime: new Date()?.toISOString(),
        provider: provider
      };

      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('isAuthenticated', 'true');

      window.location.href = '/trading-dashboard';
    } catch (err) {
      setError(`${provider} login failed. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgrade = () => {
    window.location.href = '/subscription-management';
  };

  return (
    <>
      <Helmet>
        <title>Sign In - CryptoPredict Pro | AI-Powered Trading Platform</title>
        <meta name="description" content="Sign in to your CryptoPredict Pro account to access AI-powered cryptocurrency predictions, trading signals, and professional market analysis tools." />
        <meta name="keywords" content="crypto login, trading platform, AI predictions, cryptocurrency analysis" />
        <meta property="og:title" content="Sign In - CryptoPredict Pro" />
        <meta property="og:description" content="Access your AI-powered cryptocurrency trading dashboard" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://cryptopredict.pro/user-login" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header 
          onLogin={() => {}} 
          onLogout={() => {}} 
          onUpgrade={handleUpgrade}
        />

        <main className="pt-16">
          <div className="container mx-auto px-4 py-12">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-start">
                {/* Left Column - Login Form */}
                <div className="space-y-8">
                  <LoginForm
                    onLogin={handleLogin}
                    isLoading={isLoading}
                    error={error}
                  />
                  
                  <SocialLogin
                    onSocialLogin={handleSocialLogin}
                    isLoading={isLoading}
                  />
                </div>

                {/* Right Column - Trust Signals & Upgrade */}
                <div className="space-y-8">
                  <TrustSignals />
                  
                  {showUpgradePrompt && (
                    <UpgradePrompt onUpgrade={handleUpgrade} />
                  )}
                </div>
              </div>

              {/* Demo Credentials Info */}
              <div className="mt-12 max-w-md mx-auto">
                <div className="bg-accent/10 border border-accent/20 rounded-md p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-2 h-2 bg-accent rounded-full"></div>
                    <h4 className="text-sm font-medium text-accent-foreground">Demo Credentials</h4>
                  </div>
                  <div className="space-y-1 text-xs text-muted-foreground">
                    <p><strong>Email:</strong> {mockCredentials?.email}</p>
                    <p><strong>Password:</strong> {mockCredentials?.password}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-card border-t border-border py-8">
          <div className="container mx-auto px-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                © {new Date()?.getFullYear()} CryptoPredict Pro. All rights reserved.
              </p>
              <div className="flex justify-center space-x-6 mt-4">
                <button className="text-xs text-muted-foreground hover:text-foreground transition-standard">
                  Privacy Policy
                </button>
                <button className="text-xs text-muted-foreground hover:text-foreground transition-standard">
                  Terms of Service
                </button>
                <button className="text-xs text-muted-foreground hover:text-foreground transition-standard">
                  Support
                </button>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default UserLogin;